// @greenlight-spec: 59-platform-agent.md#the-harness-seam-agentharness
import * as React from 'react';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { HARNESS_ADAPTERS, INFERENCE_SOURCES, type HarnessAdapter } from './fixtures';

const readySources = (a: HarnessAdapter) =>
  INFERENCE_SOURCES.filter((s) => s.status === 'ready' && a.supports(s));

const blockedReason = (a: HarnessAdapter): string | null => {
  if (a.evaluatedAgents === 0) return 'not available in this release (no evaluated agents)';
  if (readySources(a).length === 0) return 'no ready inference source';
  return null;
};

export function AdapterDialog({
  open,
  onOpenChange,
  currentId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentId: string;
}): React.JSX.Element {
  const [picked, setPicked] = React.useState(currentId);
  React.useEffect(() => {
    if (open) setPicked(currentId);
  }, [open, currentId]);
  const target = HARNESS_ADAPTERS.find((a) => a.id === picked);
  const others = HARNESS_ADAPTERS.filter((a) => a.id !== currentId);
  const consequences: { text: string; ok: boolean }[] = [
    { text: 'Applies to new sessions only', ok: true },
    { text: 'Sessions that can be resumed keep the adapter that produced them', ok: true },
    { text: 'Agents are offered only on adapters this release has evaluated them for', ok: true },
    ...others.map((a) => {
      const n = readySources(a).length;
      return {
        text:
          n > 0
            ? `Needs a ready inference source the adapter supports — ${n} ready for ${a.label}`
            : `Needs a ready inference source the adapter supports — none ready for ${a.label}`,
        ok: n > 0,
      };
    }),
  ];
  const canConfirm = !!target && target.id !== currentId && blockedReason(target) === null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Change the harness adapter</DialogTitle>
          <DialogDescription className="sr-only">
            Adapters and what changing one affects
          </DialogDescription>
        </DialogHeader>

        <div role="radiogroup" aria-label="Harness adapter" className="space-y-1.5">
          {HARNESS_ADAPTERS.map((a) => {
            const blocked = a.id !== currentId ? blockedReason(a) : null;
            const selected = picked === a.id;
            return (
              <button
                key={a.id}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={blocked !== null}
                onClick={() => setPicked(a.id)}
                className={cn(
                  'flex w-full items-baseline justify-between gap-3 rounded-gl-icon border border-border px-3 py-2.5 text-left text-[13px] disabled:cursor-not-allowed disabled:opacity-60',
                  selected && 'bg-muted font-semibold ring-1 ring-input',
                )}
              >
                <span>
                  {a.label}
                  {a.id === currentId && (
                    <span className="font-normal text-muted-foreground"> (current)</span>
                  )}
                  {blocked && (
                    <span className="font-normal text-muted-foreground"> — {blocked}</span>
                  )}
                </span>
                <span className="shrink-0 font-mono text-[11px] font-normal text-muted-foreground">
                  {a.version}
                </span>
              </button>
            );
          })}
        </div>

        <ul className="space-y-1.5 text-[12.5px]">
          {consequences.map((c) => (
            <li key={c.text} className="flex items-start gap-2">
              {c.ok ? (
                <CircleCheck className="mt-0.5 size-3.5 shrink-0 text-slate" aria-hidden />
              ) : (
                <CircleAlert className="mt-0.5 size-3.5 shrink-0 text-gl-amber-text" aria-hidden />
              )}
              {c.text}
            </li>
          ))}
        </ul>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canConfirm}
            onClick={() => {
              onOpenChange(false);
              toast.success(`Harness adapter changed to ${target?.label}`);
            }}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
